import { useCallback, useEffect, useRef, useState } from 'react';
import { useFormApi } from 'informed';
import { useReduxState } from 'hooks';
import { TextArea } from '.';

import type { TextAreaProps } from './TextArea';

interface Props extends TextAreaProps {
  validateJSON?: boolean | ((value: string) => boolean);
  validateFunction?: boolean;
  validateFunctionParams?: unknown[];
}

const Code = ({
  style,
  validateJSON,
  validateFunction,
  validateFunctionParams,
  ...props
}: Props) => {
  const formApi = useFormApi();

  const { theme } = useReduxState();

  const validationTimeout = useRef<NodeJS.Timeout>(null);

  const [errorState, setErrorState] = useState(null);

  useEffect(() => {
    return () => {
      clearTimeout(validationTimeout.current);
    };
  }, []);

  const handleKeyDown: React.KeyboardEventHandler<HTMLTextAreaElement> =
    useCallback((ev) => {
      const el = ev?.target as HTMLTextAreaElement;
      if (!el) return;

      if (ev.key === 'Tab') {
        ev.preventDefault();
        const start = el.selectionStart;
        const end = el.selectionEnd;

        let newValue = '';

        // split value into lines
        const lines = el.value.split(/\r?\n|\r/);
        let startIndex = 0;
        let endIndex = 0;

        let addedCharacters = 0;

        for (let i = 0; i < lines.length; i++) {
          let line = lines[i];
          if (i > 0) startIndex += lines[i - 1].length + 1;
          endIndex = startIndex + line.length;
          // check if line is part of selection
          if (
            (start >= startIndex && start < endIndex) ||
            (end >= startIndex && end <= endIndex) ||
            (start < startIndex && end > endIndex)
          ) {
            // if shift is pressed simultaneously, remove up to two spaces if
            // they exist at the start of the line
            // if shift is not pressed, add two spaces to start of line
            if (ev.shiftKey) {
              if (line[0] === ' ') {
                line = line.slice(1);
                addedCharacters -= 1;
              }
              if (line[0] === ' ') {
                line = line.slice(1);
                addedCharacters -= 1;
              }
            } else {
              line = `  ${line}`;
              addedCharacters += 2;
            }
          }
          newValue += line + (i + 1 < lines.length ? '\n' : '');
        }

        el.value = newValue;

        // update cursor position
        el.selectionStart = start + Math.min(2, Math.max(-2, addedCharacters));
        el.selectionEnd = end + addedCharacters;
      }

      if (ev.key === 'Enter') {
        ev.preventDefault();
        const start = el.selectionStart;
        const end = el.selectionEnd;

        // split value into lines
        const lines = el.value.split(/\r?\n|\r/);
        let startIndex = 0;
        let endIndex = 0;
        let spaces = 0;

        // find the current line
        for (let i = 0; i < lines.length; i++) {
          const line = lines[i];
          if (i > 0) startIndex += lines[i - 1].length + 1;
          endIndex = startIndex + line.length;
          if (start >= startIndex && start <= endIndex) {
            // get number of spaces at start of line
            spaces = line.search(/\S|$/);
            break;
          }
        }

        // update value
        el.value =
          el.value.slice(0, start) +
          '\n' +
          new Array(spaces + 1).join(' ') +
          el.value.slice(end, el.value.length);

        // update cursor position
        const position = start + 1 + spaces;
        el.selectionStart = position;
        el.selectionEnd = position;
      }
    }, []);

  const validateJSONData = (value: string) => {
    let errorStatus = null;
    try {
      if (value) {
        if (typeof validateJSON === 'function') {
          const isValid = validateJSON(value);
          if (!isValid) errorStatus = 'Invalid JSON';
        } else {
          JSON.parse(value);
        }
      }
    } catch (error) {
      errorStatus = error?.message || 'Invalid JSON';
    }
    setErrorState(errorStatus);
    return errorStatus ? errorStatus : undefined;
  };

  const validateFunctionData = (value: string) => {
    clearTimeout(validationTimeout.current);

    validationTimeout.current = setTimeout(async () => {
      let errorStatus = null;
      try {
        const evaluated = eval(value);
        const type = typeof evaluated;

        // Accept empty value
        const isEmpty = !value || value.length === 0;
        if (!isEmpty) {
          // For non-empty values require the type to be a function
          if (type !== 'function') throw new Error('type is not function');

          // Expect the function to not crash
          let hasReturnValue = evaluated(...(validateFunctionParams || [{}]));
          if (hasReturnValue instanceof Promise) {
            hasReturnValue = await hasReturnValue;
          }

          // Expect the function to have a not-empty return value
          if (hasReturnValue === null || hasReturnValue === undefined) {
            throw new Error('should have return value');
          }
        }
      } catch (error) {
        errorStatus = `Invalid Function${
          error?.message ? ': ' + error?.message : ''
        }`;
      }

      // handle error state in form
      if (props.field) {
        formApi.setError(props.field, errorStatus ?? undefined);
      }
      setErrorState(errorStatus);
    }, 300);

    return undefined;
  };

  return (
    <TextArea
      autoGrow={true}
      error={errorState}
      onKeyDown={handleKeyDown}
      style={{
        backgroundColor: theme.lightGrey,
        border: 0,
        fontFamily: 'monospace',
        ...style
      }}
      {...(validateJSON
        ? { validate: validateJSONData, validateOnChange: true }
        : validateFunction
        ? { validate: validateFunctionData, validateOnChange: true }
        : {})}
      {...props}
    />
  );
};

export default Code;
